import type { GoalTypeOption } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { publicGoalQueryKeys, usePublicGoal } from "@/lib/hooks/use-public-flow/use-goals";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";
import { useT } from "@/lib/i18n/use-t";
import { GoalInputWidget } from "@/pages/flow-instances/goals/goal-input-widget";
import { T } from "./goal-input-renderer.i18n";
import type { ComponentStyleType, GoalInputConfig } from "./types";

type PublicGoalInputRendererProps = {
  component: { config?: string };
  flowInstanceId: string;
  currentGoalId: string | null;
  /** QP-187 — the flow collection's goal types (feeds the required type picker). */
  goalTypes?: GoalTypeOption[];
  isCompleted: boolean;
  hasValidationError?: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
};

export function GoalInputRenderer({
  component,
  flowInstanceId,
  currentGoalId,
  goalTypes,
  isCompleted,
  hasValidationError,
  groupNote,
}: PublicGoalInputRendererProps) {
  const t = useT(T);
  const queryClient = useQueryClient();
  const { goal, isLoading } = usePublicGoal(currentGoalId);

  // After save, invalidate goals query so snapshot data refreshes
  const handleSaveComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });
    // P5 — a goal-input change (amount/schedule/portfolioType) shifts completion.
    queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
  }, [queryClient]);

  if (!currentGoalId) {
    return (
      <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg">
        {t.noGoalAvailable}
      </div>
    );
  }

  if (isLoading || !goal) return null;

  let goalInputConfig: GoalInputConfig = {};
  try {
    goalInputConfig = component.config ? JSON.parse(component.config) : {};
  } catch {
    // Invalid config
  }

  // QP-150 — goal_input is goal-level only now (name/type/description). Schedule/amount/
  // portfolio-type moved to portfolio_input on the portfolio page.
  return (
    <GoalInputWidget
      flowInstanceId={flowInstanceId}
      goalId={currentGoalId}
      initialData={{
        name: goal.name,
        goalType: (goal as { goalType?: string | null }).goalType ?? null,
        description: (goal as { description?: string | null }).description ?? null,
      }}
      disabled={isCompleted}
      style={(goalInputConfig.style as ComponentStyleType) || "border"}
      title={goalInputConfig.title}
      description={goalInputConfig.description}
      goalTypes={goalTypes}
      showDescription={goalInputConfig.showDescription === true}
      onSaveComplete={handleSaveComplete}
      hasValidationError={hasValidationError}
      groupNote={groupNote}
    />
  );
}
